import { useQueryClient } from "@tanstack/react-query";
import { Check, FileText, Lock, TrendingUp, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Modal } from "../../components/Modal";
import { usePageHeader } from "../../components/shell/PageHeaderContext";
import { Spinner } from "../../components/Spinner";
import { apiRequest, describeApiError } from "../../lib/api";
import {
  TERM_OPTIONS, amountError, estimateMonthlyPayment, formatBytes, formatMxn, formatPhone, isValidCurp, isValidPhone, maskCurp, normalizeCurp,
  sanitizeAmount, sanitizePhone
} from "../../lib/loans";
import { deleteLoanDocument, uploadLoanDocument, useLoanConfig, useMyLoanApplications } from "../../lib/queries";
import type { ApiEnvelope, LoanApplication, LoanConfig, LoanDocument, LoanTerm, User } from "../../types";
import { LegalSheet, StepBar } from "./LoanShared";
import { ButtonLabel } from "../../components/ButtonLabel";
import { RegulatedNote } from "../../components/Cards";

type Sheet = "auth" | "privacy" | "edit" | null;

interface Contact { fullName: string; email: string; phone: string }

/**
 * "Solicitud de crédito · Travel now, pay later" — the two-step application (design 1a/1b).
 *
 * Step 1 collects amount, term, CURP, the Constancia de Situación Fiscal PDF and the credit-bureau
 * authorisation; the primary action stays disabled until every field is valid. Step 2 confirms the
 * contact data from the account (editable, with the profile write-back the user story requires),
 * shows the summary and the legal clarification, and submits exactly once. Going back keeps every
 * value and the uploaded document.
 */
export function LoanApplicationPage() {
  const config = useLoanConfig();
  const applications = useMyLoanApplications();
  // Decided once, when the list first arrives: the application this flow itself creates must not
  // bounce the user to the status page before they have seen the "Solicitud enviada" screen.
  const openOnArrival = useRef<string | null | undefined>(undefined);
  if (applications.data && openOnArrival.current === undefined) {
    openOnArrival.current = applications.data.find((application) => application.status === "submitted" || application.status === "in_review")?.id ?? null;
  }
  if (config.isLoading || applications.isLoading) return <div className="page-state"><Spinner label="Cargando" /></div>;
  if (!config.data?.enabled) return <Navigate to="/credito" replace />;
  if (openOnArrival.current) return <Navigate to={`/credito/solicitudes/${openOnArrival.current}`} replace />;
  return <ApplicationFlow config={config.data} />;
}

function ApplicationFlow({ config }: { config: LoanConfig }) {
  const { user, applyUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2>(1);
  const [amount, setAmount] = useState("");
  const [term, setTerm] = useState<LoanTerm | null>(null);
  const [curp, setCurp] = useState("");
  const [touched, setTouched] = useState<{ amount?: boolean; curp?: boolean }>({});
  const [document, setDocument] = useState<LoanDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [contact, setContact] = useState<Contact>({ fullName: user?.fullName ?? "", email: user?.email ?? "", phone: user?.phone ?? "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string[] | null>(null);
  const [done, setDone] = useState<LoanApplication | null>(null);
  // One key per submission attempt: a retry after a network error returns the application already
  // created instead of a second one. Only editing and coming back to step 2 mints a new key.
  const idempotencyKey = useRef(crypto.randomUUID());
  const fileInput = useRef<HTMLInputElement>(null);

  usePageHeader(
    done ? "Solicitud enviada" : "Solicitud de crédito",
    done ? "Viaja ahora · paga después" : `Paso ${step} de 2 · Viaja ahora · paga después`
  );
  useEffect(() => { window.scrollTo(0, 0); }, [step, done]);

  const amountNumber = Number(amount || 0);
  const amountMessage = amountError(amount, config.minAmount, config.maxAmount);
  const amountOk = amountMessage === null;
  const curpOk = isValidCurp(curp);
  const curpMessage = !curp ? "Ingresa tu CURP." : curpOk ? null : "Revisa tu CURP. Debe contener 18 caracteres y tener un formato válido.";
  const phoneOk = isValidPhone(contact.phone);
  const stepOneValid = amountOk && term !== null && curpOk && document !== null && authorized;
  const estimate = useMemo(
    () => (amountOk && term ? estimateMonthlyPayment(amountNumber, term, config.annualRatePercent) : 0),
    [amountOk, term, amountNumber, config.annualRatePercent]
  );
  const missing = [
    !amountOk && "monto", !term && "plazo", !curpOk && "CURP", !document && "constancia", !authorized && "autorización"
  ].filter((item): item is string => Boolean(item));

  // --- Step 1 handlers ----------------------------------------------------------------------
  const pickFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setFileError(null);
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { setFileError("El archivo debe estar en formato PDF."); return; }
    if (file.size > config.documentMaxBytes) { setFileError(`El archivo supera el máximo de ${formatBytes(config.documentMaxBytes)}.`); return; }
    setUploading(true);
    const previous = document;
    try {
      const uploaded = await uploadLoanDocument(file);
      setDocument(uploaded);
      if (previous) void deleteLoanDocument(previous.id).catch(() => undefined);
    } catch (cause) {
      setFileError(describeApiError(cause, "No pudimos cargar el archivo. Inténtalo nuevamente.").join(" "));
    } finally { setUploading(false); }
  };

  const removeFile = () => {
    const current = document;
    setDocument(null); setFileError(null);
    if (current) void deleteLoanDocument(current.id).catch(() => undefined);
  };

  const continueToReview = (event: FormEvent) => {
    event.preventDefault();
    setTouched({ amount: true, curp: true });
    if (!stepOneValid) return;
    setSubmitError(null);
    setStep(2);
  };

  const backToEdit = () => {
    idempotencyKey.current = crypto.randomUUID();
    setSubmitError(null);
    setStep(1);
  };

  // --- Step 2 handlers ----------------------------------------------------------------------
  const submit = async () => {
    if (submitting || !document || !term) return;
    if (!phoneOk) { setSubmitError(["Agrega un teléfono de contacto para que Peso a Peso pueda comunicarse contigo."]); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      const result = await apiRequest<ApiEnvelope<{ application: LoanApplication }>>("/api/v1/loans/applications", {
        method: "POST",
        headers: { "idempotency-key": idempotencyKey.current },
        body: JSON.stringify({
          amount: amountNumber, termMonths: term, curp: normalizeCurp(curp), documentId: document.id,
          contact: { fullName: contact.fullName.trim(), email: contact.email.trim(), phone: sanitizePhone(contact.phone) },
          authorizationAccepted: true
        })
      });
      await queryClient.invalidateQueries({ queryKey: ["loans"] });
      setDone(result.data.application);
    } catch (cause) {
      setSubmitError(describeApiError(cause, "No pudimos enviar tu solicitud. Inténtalo de nuevo."));
    } finally { setSubmitting(false); }
  };

  if (done) return <SubmittedScreen application={done} legalNotice={config.legalNotice} />;

  const rate = `${config.annualRatePercent}%`;

  return (
    <div className="page-container loan-page">
      <header className="loan-flow-head">
        <div>
          <span className="loan-kicker">Solicitud de crédito · paso {step}</span>
          <h2>{step === 1 ? "Haz ese viaje realidad" : "Confirma tus datos"}</h2>
          <p>{step === 1 ? "Cuéntanos cuánto necesitas para hacerlo posible." : "Peso a Peso utilizará estos datos para contactarte y dar seguimiento a tu solicitud."}</p>
        </div>
        <span className="loan-step-pill">Paso {step} de 2</span>
      </header>
      <StepBar step={step} />

      <div className="loan-grid">
        {step === 1 ? (
          <form className="card loan-form" onSubmit={continueToReview} noValidate>
            <div className="field-block">
              <label htmlFor="loan-amount">¿Cuánto necesitas para tu viaje?</label>
              <div className={`money-input loan-money${touched.amount && !amountOk ? " invalid" : amountOk ? " valid" : ""}`}>
                <span aria-hidden="true">$</span>
                <input
                  id="loan-amount" inputMode="numeric" autoComplete="off" placeholder="0" required
                  value={amount ? Number(amount).toLocaleString("en-US") : ""}
                  onChange={(event) => setAmount(sanitizeAmount(event.target.value))}
                  onBlur={() => setTouched((current) => ({ ...current, amount: true }))}
                  aria-describedby="loan-amount-help" aria-invalid={touched.amount && !amountOk}
                />
                <span>MXN</span>
              </div>
              <span id="loan-amount-help" className={`field-hint${touched.amount && amountMessage ? " error" : ""}`} role={touched.amount && amountMessage ? "alert" : undefined}>
                {touched.amount && amountMessage ? amountMessage : `Puedes solicitar entre ${formatMxn(config.minAmount)} y ${formatMxn(config.maxAmount)}.`}
              </span>
            </div>

            <div className="field-block">
              <span className="field-label loan-label" id="loan-term-label">¿Qué plazo prefieres?</span>
              <div className="loan-terms" role="radiogroup" aria-labelledby="loan-term-label">
                {TERM_OPTIONS.filter((option) => config.termOptions.includes(option.months)).map((option) => (
                  <label key={option.months} className={`loan-term${term === option.months ? " selected" : ""}`}>
                    <input type="radio" name="term" value={option.months} checked={term === option.months} onChange={() => setTerm(option.months)} />
                    <strong>{option.months} <small>meses</small></strong>
                    <span>{option.hint}</span>
                    {term === option.months && <Check className="loan-term-check" size={16} strokeWidth={3} aria-hidden="true" />}
                  </label>
                ))}
              </div>
              <span className="field-hint">Selecciona una opción. El plazo final estará sujeto a la evaluación de Peso a Peso.</span>
            </div>

            {config.showPaymentEstimate && amountOk && term && (
              <div className="loan-estimate">
                <span className="loan-estimate-icon"><TrendingUp size={18} aria-hidden="true" /></span>
                <div>
                  <span className="loan-estimate-label">Pago mensual estimado</span>
                  <strong translate="no">{formatMxn(estimate)}</strong>
                  <small>Referencia con tasa {rate} anual. Condiciones finales las define Peso a Peso.</small>
                </div>
              </div>
            )}

            <div className="field-block">
              <label htmlFor="loan-curp">CURP</label>
              <input
                id="loan-curp" className={`standalone-input loan-curp${touched.curp && curpMessage ? " invalid" : curpOk ? " valid" : ""}`}
                value={curp} maxLength={18} placeholder="Ingresa tu CURP" autoComplete="off" autoCapitalize="characters" spellCheck={false}
                onChange={(event) => setCurp(normalizeCurp(event.target.value))}
                onBlur={() => setTouched((current) => ({ ...current, curp: true }))}
                aria-describedby="loan-curp-help" aria-invalid={touched.curp && Boolean(curpMessage)}
              />
              <div className="loan-hint-row">
                <span id="loan-curp-help" className={`field-hint${touched.curp && curpMessage ? " error" : ""}`} role={touched.curp && curpMessage ? "alert" : undefined}>
                  {touched.curp && curpMessage ? curpMessage : "Necesitamos tu CURP para identificarte y procesar tu solicitud."}
                </span>
                <span className="loan-counter" translate="no">{curp.length}/18</span>
              </div>
            </div>

            <div className="field-block">
              <span className="field-label loan-label">Constancia de Situación Fiscal</span>
              <span className="field-hint">Sube una constancia emitida durante los últimos 2 meses. PDF · máximo {formatBytes(config.documentMaxBytes)}.</span>
              <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={(event) => void pickFile(event)} hidden aria-label="Subir constancia" />
              {document ? (
                <>
                  <div className="loan-file ok">
                    <span className="loan-file-icon"><Check size={18} strokeWidth={2.6} aria-hidden="true" /></span>
                    <div>
                      <strong>Constancia cargada correctamente.</strong>
                      <span translate="no">{document.originalName ?? "constancia.pdf"} · {formatBytes(document.bytes)}</span>
                    </div>
                  </div>
                  <div className="inline-actions loan-file-actions">
                    <button type="button" className="secondary-button small" onClick={() => fileInput.current?.click()} disabled={uploading}>{uploading ? <Spinner /> : null} Reemplazar</button>
                    <button type="button" className="link-button danger" onClick={removeFile} disabled={uploading}><X size={14} aria-hidden="true" /> Eliminar</button>
                  </div>
                </>
              ) : (
                <button type="button" className={`loan-upload${fileError ? " invalid" : ""}`} onClick={() => fileInput.current?.click()} disabled={uploading}>
                  <ButtonLabel busy={uploading} busyLabel="Cargando constancia…" label="Subir constancia" icon={<Upload size={18} aria-hidden="true" />} iconFirst />
                </button>
              )}
              {fileError && <span className="field-hint error" role="alert">{fileError}</span>}
            </div>

            <div className={`loan-authorization${authorized ? " on" : ""}`}>
              <label className="check-row">
                <input type="checkbox" checked={authorized} onChange={(event) => setAuthorized(event.target.checked)} aria-describedby="loan-auth-more" />
                <span>Autorizo expresamente a {config.lenderName.replace(/\.$/, "")}, a consultar mi historial crediticio ante las Sociedades de Información Crediticia para evaluar mi solicitud de crédito.</span>
              </label>
              <button type="button" id="loan-auth-more" className="link-button" onClick={() => setSheet("auth")}>Consultar autorización completa</button>
            </div>
            {touched.amount && !authorized && missing.length === 1 && (
              <div className="form-error" role="alert">Para continuar, revisa y acepta la autorización para consultar tu historial crediticio.</div>
            )}
            <button type="submit" className="primary-button full loan-cta mobile-only" disabled={!stepOneValid}>Continuar</button>
            <button type="button" className="text-link center mobile-only" onClick={() => setSheet("privacy")}>Consulta nuestro Aviso de Privacidad</button>
          </form>
        ) : (
          <div className="loan-form-column">
            <section className="card loan-contact">
              <div className="loan-card-head">
                <span className="rail-title">Datos de contacto · cuenta Aventuria</span>
                <button type="button" className="link-button" onClick={() => setSheet("edit")}>Editar</button>
              </div>
              <dl className="loan-contact-grid">
                <div><dt>Nombre completo</dt><dd>{contact.fullName}</dd></div>
                <div><dt>Correo electrónico</dt><dd>{contact.email}</dd></div>
                <div><dt>Teléfono</dt><dd translate="no" className={phoneOk ? "" : "missing"}>{phoneOk ? formatPhone(sanitizePhone(contact.phone)) : "Falta tu teléfono"}</dd></div>
              </dl>
              {!phoneOk && <span className="field-hint error">Peso a Peso necesita un teléfono para contactarte. Usa «Editar» para agregarlo.</span>}
            </section>

            <section className="loan-summary-card">
              <span className="loan-offer-label">Monto solicitado</span>
              <strong className="loan-summary-amount" translate="no">{formatMxn(amountNumber)}</strong>
              <dl className="loan-summary-rows">
                <div><dt>Plazo preferido</dt><dd>{term} meses</dd></div>
                <div><dt>CURP</dt><dd translate="no">{maskCurp(curp)}</dd></div>
                <div><dt>Constancia fiscal</dt><dd className="ok"><Check size={13} strokeWidth={3} aria-hidden="true" /> {document?.originalName ?? "Cargada"}</dd></div>
              </dl>
            </section>

            <p className="loan-legal">{config.legalNotice}</p>
            {submitError && <div className="form-error" role="alert">{submitError.join(" ")}</div>}
            <div className="loan-submit mobile-only">
              <button type="button" className="primary-button full loan-cta" onClick={() => void submit()} disabled={submitting} aria-busy={submitting}>
                <ButtonLabel busy={submitting} busyLabel="Enviando solicitud…" label="Enviar solicitud" />
              </button>
              <button type="button" className="secondary-button full" onClick={backToEdit} disabled={submitting}>Regresar y editar</button>
            </div>
          </div>
        )}

        <aside className="loan-aside">
          <div className="card loan-aside-card">
            <span className="rail-title">Tu solicitud</span>
            <strong className="loan-aside-amount" translate="no">{amountOk ? formatMxn(amountNumber) : "—"}</strong>
            <span className="loan-aside-term">{term ? `Plazo preferido de ${term} meses` : "Selecciona monto y plazo"}</span>
            {config.showPaymentEstimate && amountOk && term ? (
              <div className="loan-aside-estimate">
                <span>Pago mensual estimado</span>
                <strong translate="no">{formatMxn(estimate)}</strong>
                <small>Cálculo de referencia con tasa {rate} anual. La tasa, el CAT y el plazo finales los determina Peso a Peso tras su evaluación.</small>
              </div>
            ) : null}
            <ul className="loan-checklist">
              {[
                ["Monto dentro del rango", amountOk], ["Plazo seleccionado", term !== null], ["CURP con formato válido", curpOk],
                ["Constancia en PDF", document !== null], ["Autorización de historial", authorized]
              ].map(([label, ok]) => (
                <li key={String(label)} className={ok ? "ok" : ""}><span><Check size={12} strokeWidth={3} aria-hidden="true" /></span>{label}</li>
              ))}
            </ul>
            {step === 1 ? (
              <>
                <span className={`loan-missing${stepOneValid ? " ready" : ""}`}>{stepOneValid ? "Todo listo para continuar" : `Falta: ${missing.join(" · ")}`}</span>
                <button type="button" className="primary-button full loan-cta" onClick={continueToReview} disabled={!stepOneValid}>Continuar</button>
                <button type="button" className="text-link center" onClick={() => setSheet("privacy")}>Consulta nuestro Aviso de Privacidad</button>
              </>
            ) : (
              <>
                <button type="button" className="primary-button full loan-cta" onClick={() => void submit()} disabled={submitting} aria-busy={submitting}>
                  <ButtonLabel busy={submitting} busyLabel="Enviando solicitud…" label="Enviar solicitud" />
                </button>
                <button type="button" className="secondary-button full" onClick={backToEdit} disabled={submitting}>Regresar y editar</button>
              </>
            )}
            <span className="loan-secure"><Lock size={12} aria-hidden="true" /> Tus datos se envían cifrados a Peso a Peso Credipyme · Aventuria SAPI de CV</span>
          </div>
        </aside>
      </div>

      <LegalSheet
        open={sheet === "auth"} title="Autorización completa" body={config.authorizationText} onClose={() => setSheet(null)}
        action={<button type="button" className="primary-button solid" onClick={() => { setAuthorized(true); setSheet(null); }}><Check size={16} aria-hidden="true" /> He leído y autorizo la consulta</button>}
      />
      <LegalSheet open={sheet === "privacy"} title="Aviso de Privacidad" body={config.privacyText} onClose={() => setSheet(null)} />
      <EditContactSheet
        open={sheet === "edit"} contact={contact} user={user} onClose={() => setSheet(null)}
        onSaved={(next, updatedUser) => { setContact(next); if (updatedUser) applyUser(updatedUser); setSheet(null); }}
      />
      <Link to="/credito" className="text-link center loan-cancel">Cancelar y volver</Link>
      <RegulatedNote kind="credit" />
    </div>
  );
}

/**
 * "Editar datos de contacto". Name and phone are also written to the Aventuria profile, which the
 * sheet says before saving. The account e-mail is the sign-in identity, so it is not changed here:
 * a different address entered below is used for this application only.
 */
function EditContactSheet({ open, contact, user, onClose, onSaved }: {
  open: boolean;
  contact: Contact;
  user: User | null;
  onClose(): void;
  onSaved(next: Contact, updatedUser: User | null): void;
}) {
  const [draft, setDraft] = useState<Contact>(contact);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string[] | null>(null);
  useEffect(() => { if (open) { setDraft(contact); setError(null); } }, [open, contact]);

  const save = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    const fullName = draft.fullName.trim();
    const email = draft.email.trim().toLowerCase();
    const phone = sanitizePhone(draft.phone);
    if (fullName.length < 2) { setError(["Ingresa tu nombre completo."]); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError(["Ingresa un correo electrónico válido."]); return; }
    if (!isValidPhone(phone)) { setError(["Ingresa un teléfono válido de 10 dígitos (puedes incluir la lada +52)."]); return; }
    setSaving(true);
    try {
      let updatedUser: User | null = null;
      const profileChanged = fullName !== (user?.fullName ?? "") || phone !== (user?.phone ?? "");
      if (profileChanged) {
        const result = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/profile", { method: "PATCH", body: JSON.stringify({ fullName, phone }) });
        updatedUser = result.data.user;
      }
      onSaved({ fullName, email, phone }, updatedUser);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos guardar tus datos."));
    } finally { setSaving(false); }
  };

  const emailDiffers = draft.email.trim().toLowerCase() !== (user?.email ?? "");
  return (
    <Modal open={open} label="Editar datos de contacto" onOpenChange={(next) => { if (!next && !saving) onClose(); }}>
      <form className="dialog-content loan-sheet" onSubmit={save}>
        <button type="button" className="dialog-close" onClick={onClose} aria-label="Cerrar" disabled={saving}><X size={18} aria-hidden="true" /></button>
        <span className="page-eyebrow">Datos de contacto</span>
        <h2>Editar datos de contacto</h2>
        <div className="dialog-form">
          <div className="field-block">
            <label htmlFor="edit-name">Nombre completo</label>
            <input id="edit-name" className="standalone-input" value={draft.fullName} onChange={(event) => setDraft({ ...draft, fullName: event.target.value })} autoComplete="name" minLength={2} maxLength={160} required />
          </div>
          <div className="field-block">
            <label htmlFor="edit-email">Correo electrónico</label>
            <input id="edit-email" className="standalone-input" type="email" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} autoComplete="email" required />
            {emailDiffers && <span className="field-hint">Este correo se usará solo para esta solicitud. El correo con el que entras a Aventuria no cambia.</span>}
          </div>
          <div className="field-block">
            <label htmlFor="edit-phone">Teléfono</label>
            <input id="edit-phone" className="standalone-input" inputMode="tel" value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} autoComplete="tel" placeholder="+52 55 0000 0000" required />
          </div>
          <div className="loan-notice">Al guardar, tu nombre y teléfono también se actualizarán en tu perfil de Aventuria.</div>
          {error && <div className="form-error" role="alert">{error.join(" ")}</div>}
          <div className="dialog-actions">
            <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="primary-button solid" disabled={saving}>{saving ? <Spinner /> : null} Guardar cambios</button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

/** "Solicitud enviada" — folio and follow-up (design 3b). */
function SubmittedScreen({ application, legalNotice }: { application: LoanApplication; legalNotice: string }) {
  const navigate = useNavigate();
  return (
    <div className="page-container">
      <div className="loan-done-grid">
        <section className="loan-done-hero submitted">
          <span className="loan-done-icon"><Check size={28} strokeWidth={2.4} aria-hidden="true" /></span>
          <h2>Solicitud enviada</h2>
          <p>Un ejecutivo de Peso a Peso se pondrá en contacto contigo con la decisión y, en su caso, las condiciones disponibles. Puedes seguir el estatus desde tu Wallet.</p>
          <div className="inline-actions">
            <button type="button" className="primary-button loan-cta waikiki" onClick={() => navigate("/wallet")}>Volver a mi Wallet</button>
            <button type="button" className="secondary-button glass" onClick={() => navigate(`/credito/solicitudes/${application.id}`)}>Ver estatus de mi solicitud</button>
          </div>
        </section>
        <aside className="card loan-folio-card">
          <span className="rail-title">Folio de solicitud</span>
          <strong className="loan-folio" translate="no">{application.folio}</strong>
          <span className="tag loan-status info">En revisión</span>
          <dl className="review-list loan-review">
            <div><dt>Monto solicitado</dt><dd translate="no">{formatMxn(application.amount)}</dd></div>
            <div><dt>Plazo preferido</dt><dd>{application.termMonths} meses</dd></div>
            <div><dt>Enviada</dt><dd>Hoy</dd></div>
          </dl>
          <p className="loan-fine"><FileText size={12} aria-hidden="true" /> {legalNotice.split(". ").slice(-1)[0]}</p>
        </aside>
      </div>
      <RegulatedNote kind="credit" />
    </div>
  );
}
